import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Stack,
  Typography,
  Button,
  TextField,
  MenuItem,
  Snackbar,
  Alert,
  Skeleton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  Avatar,
  InputAdornment,
} from '@mui/material';
import {
  AssignmentTurnedIn,
  Download,
  Email,
  FilterList,
  LocalHospital,
  Pets,
  Refresh,
  Search,
  WarningAmber,
  WhatsApp,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { Chip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import api from '../../services/api';
import {
  SMART_COLORS as COLORS,
  smartCardSx as cardSx,
  smartTableContainerSx,
  smartTableHeadSx,
  smartTableSx,
} from '../../theme/smartStableTheme';
import { formatDateDisplay, formatDateTimeDisplay, todayDateInput } from '../../utils/dateUtils';
// T6: عبر services/api المركزي (مسارات نسبية، يرفق التوكن) بدل axios العام.

function MetricCard({ icon, title, value, helper, color = COLORS.gold, soft = COLORS.goldSoft }) {
  return (
    <Paper elevation={0} sx={{ ...cardSx, p: 2, minHeight: 104 }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Avatar
          sx={{
            width: 48,
            height: 48,
            bgcolor: soft,
            color,
            flexShrink: 0,
            '& svg': { fontSize: 25 },
          }}
        >
          {icon}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ color, fontWeight: 900, fontSize: 13 }}>
            {title}
          </Typography>
          <Typography variant="h5" sx={{ color: COLORS.text, fontWeight: 900 }}>
            {value}
          </Typography>
          {helper ? (
            <Typography variant="caption" sx={{ color: COLORS.muted }}>
              {helper}
            </Typography>
          ) : null}
        </Box>
      </Stack>
    </Paper>
  );
}

function countItems(rounds = []) {
  return rounds.reduce((sum, round) => sum + (Array.isArray(round.items) ? round.items.length : 0), 0);
}

export default function VetCheckupReport() {
  const { t, i18n } = useTranslation();
  const language = i18n.language === 'ar' ? 'ar' : 'en';
  const dir = language === 'ar' ? 'rtl' : 'ltr';
  const severityMeta = useMemo(
    () => ({
      critical: {
        rank: 4,
        color: 'error',
        label: t('vetCheckup.critical', 'حرجة'),
      },
      high: { rank: 3, color: 'warning', label: t('vetCheckup.high', 'عالية') },
      medium: {
        rank: 2,
        color: 'info',
        label: t('vetCheckup.medium', 'متوسطة'),
      },
      low: { rank: 1, color: 'default', label: t('vetCheckup.low', 'منخفضة') },
    }),
    [t]
  );

  const worstSeverityOf = useCallback((items = []) => {
    let best = null;
    for (const it of items) {
      const key = String(it?.severity || '').toLowerCase();
      const s = severityMeta[key];
      if (s && (!best || s.rank > best.rank)) best = s;
    }
    return best; // قد تكون null إذا ما فيه بيانات
  }, [severityMeta]);

  const anyActionRequired = useCallback(
    (items = []) => items.some((it) => Number(it?.action_required)),
    []
  );

  const viewSessionIdRef = useRef(uuidv4());
  const logAudit = useCallback(async (payload) => {
    try {
      await api.post(`/audit/log`, {
        view_session_id: viewSessionIdRef.current,
        user_id: null, // أو من الـ context/session لو متوفر
        role_id: null,
        ...payload, // <-- هذه هي المهمة
      });
    } catch (_) {
      /* لا تمنع الواجهة */
    }
  }, []);

  const [snackbar, setSnackbar] = useState({
    open: false,
    msg: '',
    sev: 'success',
  });
  const showSnack = useCallback((msg, sev = 'success') => {
    setSnackbar({ open: true, msg, sev });
  }, []);

  // يجمع أسماء الخيل من عناصر الجولة مع إزالة التكرار
  const getHorseLabel = useCallback((items) => {
    if (!Array.isArray(items) || !items.length) return '';
    const uniq = [];
    for (const it of items) {
      const name =
        it.horse_name ||
        it.horse_name_ar ||
        it.horse_name_en ||
        it.horse || // في حال أرسلت من الـ API بهذا المفتاح
        it.horse_id;
      if (name != null && !uniq.includes(name)) uniq.push(String(name));
    }
    const firstPart = uniq.slice(0, 3).join('، ');
    return firstPart + (uniq.length > 3 ? ` +${uniq.length - 3}` : '');
  }, []);

  /*const buildWhatsAppText = useCallback(
    (round, items) => {
      const horses = getHorseLabel(items);
      const procedures = items
        .map(
          (it) =>
            it.type_name_ar ||
            it.procedure_name_ar ||
            it.type_name_en ||
            it.procedure_name_en ||
            ''
        )
        .filter(Boolean)
        .join('، ');
      const notes = items.find((i) => i.notes)?.notes || '';
      const date = todayDateInput();

      return `تنبيه: تم تنفيذ الإجراءات التالية على ${
        horses || 'الخيل'
      } بتاريخ ${date}.
${procedures || '—'}${notes ? `\nملاحظات: ${notes}` : ''}`;
    },
    [getHorseLabel]
  );
*/
  // ---------- Helpers ----------
  const formatDateTime = (val) => {
    return formatDateTimeDisplay(val, '—');
  };
  const [tplKey, setTplKey] = useState('PROCEDURE_PERFORMED'); // الافتراضي

  const previewOwnerAlert = useCallback(
    async ({ horseId, round, items }) => {
      const payload = {
        service_type: 'vetclinic',
        horse_id: Number(horseId),
        horse_origin: round?.horse_origin || 'Inside',
        language: 'ar',
        template_key: tplKey, // يبقى كما هو إذا مُعرّف أعلى الكمبوننت
        items: (items || []).map((it) => ({
          procedure_name_ar: it.type_name_ar || it.procedure_name_ar || 'إجراء',
          procedure_name_en:
            it.type_name_en || it.procedure_name_en || 'Procedure',
          severity: it.severity || 'low',
          action_required: Number(it.action_required) ? 1 : 0,
          notes: it.notes || '',
        })),
      };

      const r = await api.post('/Alerts/owner-alerts/preview', payload);
      return r?.data?.body_rendered ?? '';
    },
    [tplKey]
  );

  // يبني نص واتساب من عناصر الجولة (نص بسيط وواضح)
  /*const buildWhatsAppText = (round, items) => {
    // أسماء الخيل (موجود عندك helper جاهز getHorseLabel)
    const horses = getHorseLabel(items);

    // الإجراءات (عربي أولاً ثم إنجليزي لو مفقود)
    const procedures = items
      .map(
        (it) =>
          it.type_name_ar ||
          it.procedure_name_ar ||
          it.type_name_en ||
          it.procedure_name_en ||
          ''
      )
      .filter(Boolean)
      .join('، ');

    const notes = items.find((i) => i.notes)?.notes || '';
    const date = todayDateInput();

    return `تنبيه: تم تنفيذ الإجراءات التالية على ${
      horses || 'الخيل'
    } بتاريخ ${date}.
${procedures || '—'}${notes ? `\nملاحظات: ${notes}` : ''}`;
  };*/
  // يبني نص واتساب من عناصر الجولة (نص بسيط وواضح)
  const buildWhatsAppText = useCallback(
    (round, items) => {
      // أسماء الخيل (موجود عندك helper جاهز getHorseLabel)
      const horses = getHorseLabel(items);

      // الإجراءات (عربي أولاً ثم إنجليزي لو مفقود)
      const procedures = items
        .map(
          (it) =>
            it.type_name_ar ||
            it.procedure_name_ar ||
            it.type_name_en ||
            it.procedure_name_en ||
            ''
        )
        .filter(Boolean)
        .join('، ');

      const notes = items.find((i) => i.notes)?.notes || '';
      const date = todayDateInput();

      return `تنبيه: تم تنفيذ الإجراءات التالية على ${
        horses || 'الخيل'
      } بتاريخ ${date}.
${procedures || '—'}${notes ? `\nملاحظات: ${notes}` : ''}`;
    },
    [getHorseLabel]
  );

  const sendWhatsAppForRound = useCallback(
    async (round) => {
      try {
        const list = Array.isArray(round.items) ? round.items : [];
        if (!list.length) {
          showSnack('لا توجد عناصر في هذه الجولة', 'error');
          return;
        }

        // نجمع العناصر حسب horse_id (رسالة مستقلة لكل حصان/مالك)
        const groups = list.reduce((acc, it) => {
          const hid = it.horse_id || round.horse_id;
          if (!hid) return acc;
          (acc[hid] ||= []).push(it);
          return acc;
        }, {});

        let opened = 0;

        for (const [horseId, items] of Object.entries(groups)) {
          // 1) رقم المالك من الخادم (كما تفعل الآن)
          const r = await api.get(
            `/Alerts/owner-alerts/recipient/${horseId}`
          );
          const phone = r?.data?.owner_mobile;
          if (!phone) {
            showSnack('لا يوجد رقم جوال للمالك', 'warning');
            continue;
          }

          // 2) النص الموحّد من الخادم (نفس القالب المستخدم للإيميل)
          let text = '';
          try {
            text = await previewOwnerAlert({ horseId, round, items });
          } catch {
            // Fallback (اختياري): لو فشل الـpreview لأي سبب
            text = buildWhatsAppText(round, items);
          }

          // 3) افتح wa.me بنفس النص الموحّد
          window.open(waLink(phone, text), '_blank', 'noopener,noreferrer');
          opened++;
        }

        if (opened) showSnack('تم فتح واتساب لإرسال الرسائل');
        else showSnack('تعذر فتح أي رسالة واتساب', 'warning');
      } catch (e) {
        console.error(e);
        showSnack('تعذر فتح واتساب', 'error');
      }
    },
    [showSnack, buildWhatsAppText, previewOwnerAlert]
  );

  // يجمع أسماء الخيل من عناصر الجولة مع إزالة التكرار

  // ---------- Filters ----------
  const today = useMemo(() => todayDateInput(), []);
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  const [stableId, setStableId] = useState('');
  const [roomId, setRoomId] = useState('');
  const [vetId, setVetId] = useState('');
  const [q, setQ] = useState('');
  const [sending, setSending] = useState(new Set());
  const [tpls, setTpls] = useState([]);

  // ---------- Datasets / Dropdowns ----------
  const [rooms, setRooms] = useState([]);
  const [loadingRooms, setLoadingRooms] = useState(false);

  const [vets, setVets] = useState([]);
  const [loadingVets, setLoadingVets] = useState(false);

  // ---------- Report Data ----------
  const [rounds, setRounds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  // ---------- Abort Controllers ----------
  const roomsAbortRef = useRef(null);
  const vetsAbortRef = useRef(null);
  const roundsAbortRef = useRef(null);
  // Builds a wa.me link (phone must be in full international format e.g., 9665XXXXXXXX)
  // يبني رابط wa.me (الموبايل بصيغة دولية بدون +، والنص URL-encoded)
  const waLink = (phone, text) => {
    const digits = String(phone).replace(/\D/g, '');
    return `https://wa.me/${digits}?text=${encodeURIComponent(text)}`;
  };

  // ---------- Derived: Stables & Rooms ----------
  const stables = useMemo(() => {
    const map = new Map();
    for (const r of rooms) {
      if (!map.has(r.stable_id)) map.set(r.stable_id, r.stable_name);
    }
    return Array.from(map, ([id, name]) => ({ id, name })).sort((a, b) =>
      String(a.name).localeCompare(String(b.name), 'ar', {
        sensitivity: 'base',
      })
    );
  }, [rooms]);

  const roomsOfSelectedStable = useMemo(() => {
    if (!stableId) return [];
    const list = rooms.filter((r) => r.stable_id === Number(stableId));
    const map = new Map();
    for (const r of list)
      if (!map.has(r.room_id)) map.set(r.room_id, r.room_name);
    return Array.from(map, ([id, name]) => ({ id, name })).sort((a, b) =>
      String(a.name).localeCompare(String(b.name), 'ar', { numeric: true })
    );
  }, [rooms, stableId]);

  // ---------- Pagination ----------
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };
  const pagedRounds = useMemo(
    () => rounds.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [rounds, page, rowsPerPage]
  );

  // ---------- Items Dialog ----------
  const [itemsOfRound, setItemsOfRound] = useState(null); // null أو { round, items }

  // ---------- Loaders ----------
  const loadRooms = useCallback(async () => {
    setLoadingRooms(true);
    try {
      roomsAbortRef.current?.abort();
      roomsAbortRef.current = new AbortController();
      const r = await api.get(`/rooms/rooms-with-stable-count`, {
        signal: roomsAbortRef.current.signal,
      });
      setRooms(Array.isArray(r.data) ? r.data : []);
    } catch {
      setRooms([]); // لا توقف الصفحة لو فشل
    } finally {
      setLoadingRooms(false);
    }
  }, []);

  const loadVets = useCallback(async () => {
    setLoadingVets(true);
    try {
      vetsAbortRef.current?.abort();
      vetsAbortRef.current = new AbortController();

      // نحاول عدّة مسارات، ونتجاهل 404 بهدوء
      const tryEndpoints = [
        `/users?role=Veterinarian`,
        `/users?role=vet`,
        `/vet-clinic/vets`,
      ];
      for (const url of tryEndpoints) {
        try {
          const r = await api.get(url, {
            signal: vetsAbortRef.current.signal,
          });
          const arr = Array.isArray(r.data) ? r.data : [];
          if (arr.length) {
            const norm = arr
              .map((v) => ({
                id: v.user_id || v.vet_id || v.id,
                name: v.username || v.name || v.full_name,
              }))
              .filter((v) => v.id);
            setVets(norm);
            return;
          }
        } catch {
          // ignore
        }
      }
      setVets([]);
    } finally {
      setLoadingVets(false);
    }
  }, []);

  const loadRounds = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      roundsAbortRef.current?.abort();
      roundsAbortRef.current = new AbortController();
      const params = {
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        stable_id: stableId || undefined,
        room_id: roomId || undefined,
        vet_user_id: vetId || undefined,
        q: q || undefined,
      };
      const r = await api.get(`/checkup/rounds`, {
        params,
        signal: roundsAbortRef.current.signal,
      });

      // تأكد دائمًا من أن items مصفوفة (ولو كانت JSON نصًا يتم تحويلها)
      const arr = Array.isArray(r.data) ? r.data : [];
      const normalized = arr.map((row) => {
        let items = row.items;
        if (typeof items === 'string') {
          try {
            items = JSON.parse(items);
          } catch {
            items = [];
          }
        }
        if (!Array.isArray(items)) items = [];
        return { ...row, items };
      });
      setRounds(normalized);
      setPage(0); // ارجع للصفحة الأولى بعد أي تحميل جديد
    } catch (e) {
      if (!(e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError')) {
        setErr(
          e?.response?.data?.message ||
            e?.response?.data?.error ||
            (e?.response?.status >= 500
              ? t('common.serverError', 'خطأ من الخادم')
              : t('vetReport.loadError', 'فشل تحميل التقرير'))
        );
      }
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, stableId, roomId, vetId, q, t]);

  // ---------- Init / Effects ----------
  useEffect(() => {
    loadRooms();
    loadVets();
    return () => {
      roomsAbortRef.current?.abort();
      vetsAbortRef.current?.abort();
      roundsAbortRef.current?.abort();
    };
  }, [loadRooms, loadVets]);
  useEffect(() => {
    logAudit({
      entity_type: 'vet_checkup_report',
      entity_id: 0,
      action: 'VIEW',
    });
  }, [logAudit]);

  useEffect(() => {
    loadRounds();
  }, [loadRounds]);

  // ---------- UI: Snackbar ----------

  const handleExportJSON = () => {
    logAudit({
      entity_type: 'vet_checkup_report',
      entity_id: 0,
      action: 'EXPORT',
    });
    try {
      const blob = new Blob([JSON.stringify(rounds, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vet-checkup-report_${dateFrom}_to_${dateTo}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      showSnack(t('common.exported', 'تم التصدير'));
    } catch {
      showSnack(t('common.exportFailed', 'فشل التصدير'), 'error');
    }
  };

  const totalItems = useMemo(() => countItems(rounds), [rounds]);
  const actionRounds = useMemo(
    () => rounds.filter((round) => anyActionRequired(round.items || [])).length,
    [anyActionRequired, rounds]
  );
  const highSeverityRounds = useMemo(
    () => rounds.filter((round) => (worstSeverityOf(round.items || [])?.rank || 0) >= 3).length,
    [rounds, worstSeverityOf]
  );
  const uniqueHorses = useMemo(() => {
    const ids = new Set();
    rounds.forEach((round) => {
      (round.items || []).forEach((item) => {
        if (item.horse_id) ids.add(String(item.horse_id));
      });
    });
    return ids.size;
  }, [rounds]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/Alerts/templates`, {
          params: { q: '' },
        });
        const arr = Array.isArray(r.data) ? r.data : [];
        // فلترة اختيارية: قوالب تدعم القنوات التي تحتاجها
        setTpls(
          arr.filter(
            (x) =>
              String(x.channels_csv || '').includes('Email') ||
              String(x.channels_csv || '').includes('WhatsApp')
          )
        );
      } catch {}
    })();
  }, []);

  /*const notifyOwnersForRoundWhatsApp = useCallback(
    async (round) => {
      const rowKey = String(round.round_id ?? round.id);
      if (sending.has(rowKey)) return;
      setSending((prev) => new Set(prev).add(rowKey));

      try {
        const token = localStorage.getItem('token');
        const itemsList = Array.isArray(round.items) ? round.items : [];
        if (!itemsList.length) {
          showSnack('لا توجد عناصر في هذه الجولة', 'error');
          return;
        }

        const groups = itemsList.reduce((acc, it) => {
          const hid = it.horse_id || round.horse_id;
          if (!hid) return acc;
          (acc[hid] ||= []).push(it);
          return acc;
        }, {});

        let ok = 0,
          fail = 0,
          skipped = 0,
          dupFound = 0;
        const sendOne = async (horseId, items, force = false) => {
          const payload = {
            service_type: 'vetclinic',
            horse_id: Number(horseId),
            horse_origin: round.horse_origin || 'Inside',
            language: 'ar',
            template_key: tplKey,
            items: items.map((it) => ({
              procedure_name_ar:
                it.type_name_ar || it.procedure_name_ar || 'إجراء',
              procedure_name_en:
                it.type_name_en || it.procedure_name_en || 'Procedure',
              severity: it.severity || 'low',
              action_required: Number(it.action_required) ? 1 : 0,
              notes: it.notes || '',
            })),
            override_channels: ['InApp', 'WhatsApp'], // ← أهم سطر
            ...(force ? { force_resend: true } : {}),
            created_by: null,
          };
          const headers = {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            'X-Idempotency-Key': `round-${rowKey}-horse-${horseId}-${
              force ? 'force' : 'normal'
            }`,
          };
          return api.post(`${apiUrl}/Alerts/owner-alerts/notify`, payload, {
            headers,
          });
        };

        for (const [horseId, items] of Object.entries(groups)) {
          try {
            await sendOne(horseId, items, false);
            ok++;
          } catch (err) {
            const status = err?.response?.status,
              data = err?.response?.data;
            if (status === 409 && data?.duplicate_today) {
              dupFound++;
              if (
                window.confirm(
                  'تم إرسال إشعار لهذا الخيل اليوم. هل تريد إعادة الإرسال؟'
                )
              ) {
                try {
                  await sendOne(horseId, items, true);
                  ok++;
                } catch {
                  fail++;
                }
              } else {
                skipped++;
              }
            } else {
              fail++;
            }
          }
        }

        if (ok && !fail)
          showSnack(
            `تم الإرسال بنجاح إلى ${ok} خيل${
              skipped ? `، وتخطّينا ${skipped}` : ''
            }`
          );
        else if (ok && fail)
          showSnack(
            `أُرسل ${ok} وفشل ${fail}${skipped ? ` وتخطّينا ${skipped}` : ''}`,
            'warning'
          );
        else showSnack('فشل الإرسال', 'error');
      } finally {
        setSending((prev) => {
          const next = new Set(prev);
          next.delete(String(rowKey));
          return next;
        });
      }
    },
    [showSnack, sending, setSending]
  );*/

  const notifyOwnersForRound = useCallback(
    async (round) => {
      // مفتاح فريد للصف (يُستخدم لتعطيل الزر مؤقتًا ومنع النقرات المتكررة)
      const rowKey = String(round.round_id ?? round.id);

      // إن كانت هذه الجولة قيد الإرسال بالفعل — تجاهل النقرات المتكررة
      if (sending.has(rowKey)) return;

      // علّم الجولة بأنها قيد الإرسال (للتعطيل المؤقت)
      setSending((prev) => {
        const next = new Set(prev);
        next.add(rowKey);
        return next;
      });

      try {
        const token = localStorage.getItem('token');
        const itemsList = Array.isArray(round.items) ? round.items : [];
        if (!itemsList.length) {
          showSnack('لا توجد عناصر في هذه الجولة', 'error');
          return;
        }

        // نجمع العناصر حسب horse_id (إشعار مستقل لكل حصان)
        const groups = itemsList.reduce((acc, it) => {
          const hid = it.horse_id || round.horse_id;
          if (!hid) return acc;
          (acc[hid] ||= []).push(it);
          return acc;
        }, {});

        let ok = 0,
          fail = 0,
          skipped = 0,
          dupFound = 0;

        const formatKSA = (iso) => {
          return formatDateTimeDisplay(iso, '');
        };

        // مُرسل واحد لكل حصان (مع خيار فرض إعادة الإرسال)
        const sendOne = async (horseId, items, force = false) => {
          const payload = {
            service_type: 'vetclinic',
            horse_id: Number(horseId),
            horse_origin: round.horse_origin || 'Inside',
            language: 'ar',
            template_key: tplKey,
            items: items.map((it) => ({
              procedure_name_ar:
                it.type_name_ar || it.procedure_name_ar || 'إجراء',
              procedure_name_en:
                it.type_name_en || it.procedure_name_en || 'Procedure',
              severity: it.severity || 'low',
              action_required: Number(it.action_required) ? 1 : 0,
              notes: it.notes || '',
            })),
            ...(force ? { force_resend: true } : {}),
            created_by: null,
          };

          const headers = {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            // مفتاح idempotency اختياري — يفيد إن كان الباك يدعمه
            'X-Idempotency-Key': `round-${rowKey}-horse-${horseId}-${
              force ? 'force' : 'normal'
            }`,
          };

          return api.post(`/Alerts/owner-alerts/notify`, payload, {
            headers,
          });
        };

        // إرسال لكل حصان مع التعامل مع التكرار اليومي (409)
        for (const [horseId, items] of Object.entries(groups)) {
          try {
            await sendOne(horseId, items, false);
            ok++;
          } catch (err) {
            const status = err?.response?.status;
            const data = err?.response?.data;

            if (status === 409 && data?.duplicate_today) {
              dupFound++;
              const lastTime = data?.last_sent_at
                ? formatKSA(data.last_sent_at)
                : '';
              const confirmMsg = lastTime
                ? `تم إرسال إشعار لهذا الخيل اليوم (${lastTime}). هل تريد إعادة الإرسال؟`
                : 'تم إرسال إشعار لهذا الخيل اليوم. هل تريد إعادة الإرسال؟';

              if (window.confirm(confirmMsg)) {
                try {
                  await sendOne(horseId, items, true);
                  ok++;
                } catch {
                  fail++;
                }
              } else {
                skipped++;
              }
            } else {
              fail++;
            }
          }
        }

        // ملخص
        if (ok && !fail) {
          showSnack(
            `تم الإرسال بنجاح إلى ${ok} خيل${
              skipped ? `، وتخطّينا ${skipped}` : ''
            }${dupFound && !skipped ? ' (تم اكتشاف سجلات مرسلة اليوم)' : ''}`
          );
        } else if (ok && fail) {
          showSnack(
            `أُرسل ${ok} وفشل ${fail}${skipped ? ` وتخطّينا ${skipped}` : ''}`,
            'warning'
          );
        } else {
          showSnack('فشل الإرسال', 'error');
        }
      } catch {
        showSnack('فشل الإرسال', 'error');
      } finally {
        // إزالة حالة الإرسال عن الصف (إعادة تمكين الزر)
        setSending((prev) => {
          const next = new Set(prev);
          next.delete(rowKey);
          return next;
        });
      }
    },
    [showSnack, sending, tplKey]
  );
  useEffect(() => {
    const saved = localStorage.getItem('tplKey');
    if (saved) setTplKey(saved);
  }, []);
  useEffect(() => {
    if (tplKey) localStorage.setItem('tplKey', tplKey);
  }, [tplKey]);

  // ---------- Render ----------
  return (
    <>
      <Box
        className="smart-page-container"
        sx={{
          minHeight: '100vh',
          bgcolor: COLORS.page,
          color: COLORS.text,
          width: '100%',
          maxWidth: 'var(--app-page-max)',
          mx: 'auto',
          overflowX: 'hidden',
          direction: dir,
        }}
      >
        <Paper elevation={0} sx={{ ...cardSx, p: { xs: 2, md: 3 }, mb: 3 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', md: 'center' }}
            justifyContent="space-between"
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h4" sx={{ color: COLORS.text, fontWeight: 900 }}>
                {t('vetReport.title', 'تقرير الجولات البيطرية')}
              </Typography>
              <Typography sx={{ color: COLORS.muted, mt: 0.5 }}>
                {t('vetReport.subtitle', 'متابعة الجولات البيطرية حسب التاريخ، الإسطبل، الغرفة، الطبيب، والحالات المسجلة.')}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} justifyContent={{ xs: 'stretch', md: 'flex-end' }}>
              <Button
                onClick={loadRounds}
                disabled={loading}
                variant="outlined"
                startIcon={dir === 'ltr' ? <Refresh /> : null}
                endIcon={dir === 'rtl' ? <Refresh /> : null}
                sx={{ borderColor: COLORS.line, color: COLORS.text, fontWeight: 900 }}
              >
                {t('common.refresh', 'تحديث')}
              </Button>
              <Button
                onClick={handleExportJSON}
                disabled={!rounds.length}
                variant="contained"
                startIcon={dir === 'ltr' ? <Download /> : null}
                endIcon={dir === 'rtl' ? <Download /> : null}
                sx={{ bgcolor: COLORS.gold, fontWeight: 900, '&:hover': { bgcolor: COLORS.goldHover } }}
              >
                {t('common.export', 'تصدير JSON')}
              </Button>
            </Stack>
          </Stack>
        </Paper>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' },
            gap: 2,
            mb: 3,
          }}
        >
          <MetricCard
            icon={<LocalHospital />}
            title={t('vetReport.rounds', 'الجولات')}
            value={rounds.length}
            helper={t('vetReport.filteredRounds', 'حسب الفلاتر الحالية')}
          />
          <MetricCard
            icon={<Pets />}
            title={t('vetReport.horsesCount', 'الخيل')}
            value={uniqueHorses}
            helper={`${totalItems} ${t('vetReport.itemsCount', 'عنصر')}`}
            color={COLORS.blue}
            soft={COLORS.blueSoft}
          />
          <MetricCard
            icon={<AssignmentTurnedIn />}
            title={t('vetReport.actionRequired', 'يتطلب إجراء')}
            value={actionRounds}
            helper={t('vetReport.roundsNeedAction', 'جولات تحتاج متابعة')}
            color={COLORS.green}
            soft={COLORS.greenSoft}
          />
          <MetricCard
            icon={<WarningAmber />}
            title={t('vetReport.highSeverity', 'حالات عالية')}
            value={highSeverityRounds}
            helper={t('vetReport.highSeverityHint', 'مرتفعة أو حرجة')}
            color={COLORS.rose}
            soft={COLORS.roseSoft}
          />
        </Box>

        <Paper elevation={0} sx={{ ...cardSx, p: 2, mb: 3 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: 'repeat(2, minmax(0, 1fr))',
                lg: '1.15fr 1fr 1fr 1fr 1fr 1fr',
              },
              gap: 1.5,
              alignItems: 'center',
            }}
          >
            <TextField
              size="small"
              placeholder={t('vetReport.searchPH', 'بحث (خيل/ملاحظة/حالة)')}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              select
              size="small"
              label={t('vetReport.template', 'القالب')}
              value={tplKey}
              onChange={(e) => setTplKey(e.target.value)}
            >
              {tpls.map((template) => (
                <MenuItem key={template.template_key} value={template.template_key}>
                  {template.template_key}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label={t('common.fromDate', 'من تاريخ')}
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              size="small"
              label={t('common.toDate', 'إلى تاريخ')}
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              select
              size="small"
              label={t('accommodationList.stable', 'الإسطبل')}
              value={stableId}
              onChange={(e) => {
                setStableId(e.target.value);
                setRoomId('');
              }}
            >
              <MenuItem value="">
                {loadingRooms ? t('common.loading', 'جاري التحميل...') : t('common.all', 'الكل')}
              </MenuItem>
              {stables.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              disabled={!stableId}
              label={t('vetReport.room', 'الغرفة')}
              value={roomId}
              onChange={(e) => setRoomId(e.target.value)}
            >
              <MenuItem value="">{t('common.all', 'الكل')}</MenuItem>
              {roomsOfSelectedStable.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label={t('vetReport.vet', 'الطبيب')}
              value={vetId}
              onChange={(e) => setVetId(e.target.value)}
              sx={{ gridColumn: { lg: 'span 1' } }}
            >
              <MenuItem value="">
                {loadingVets ? t('common.loading', 'جاري التحميل...') : t('common.all', 'الكل')}
              </MenuItem>
              {vets.map((v) => (
                <MenuItem key={v.id} value={v.id}>
                  {v.name || v.id}
                </MenuItem>
              ))}
            </TextField>
            <Button
              variant="contained"
              onClick={loadRounds}
              startIcon={dir === 'ltr' ? <FilterList /> : null}
              endIcon={dir === 'rtl' ? <FilterList /> : null}
              sx={{
                bgcolor: COLORS.gold,
                fontWeight: 900,
                minHeight: 40,
                '&:hover': { bgcolor: COLORS.goldHover },
              }}
            >
              {t('common.apply', 'تطبيق')}
            </Button>
          </Box>
        </Paper>

        {/* Error banner */}
        {err && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {err}
          </Alert>
        )}

        <Paper elevation={0} sx={{ ...cardSx, overflow: 'hidden' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 2, py: 1.5 }}>
            <Typography sx={{ color: COLORS.text, fontWeight: 900 }}>
              {t('vetReport.results', 'نتائج التقرير')}
            </Typography>
            <Chip
              label={`${rounds.length} ${t('vetReport.rounds', 'الجولات')}`}
              sx={{ bgcolor: COLORS.goldSoft, color: COLORS.text, fontWeight: 900 }}
            />
          </Stack>
          {loading ? (
            <Box sx={{ p: 2 }}>
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} variant="rounded" height={64} sx={{ mb: 1 }} />
              ))}
            </Box>
          ) : rounds.length === 0 ? (
            <Alert severity="info" sx={{ m: 2 }}>
              {t('vetReport.noData', 'لا توجد بيانات للفترة/المرشحات المحددة')}
            </Alert>
          ) : (
            <>
              <TableContainer sx={{ ...smartTableContainerSx, width: '100%' }}>
                <Table size="small" sx={{ ...smartTableSx, minWidth: 1180 }}>
                  <TableHead sx={smartTableHeadSx}>
                <TableRow>
                  <TableCell sx={{ width: 64 }}>#</TableCell>
                  <TableCell>{t('common.date', 'التاريخ')}</TableCell>
                  <TableCell>
                    {t('accommodationList.stable', 'الإسطبل')}
                  </TableCell>
                  <TableCell>{t('vetReport.room', 'الغرفة')}</TableCell>
                  <TableCell>{t('vetReport.horse', 'الخيل')}</TableCell>
                  <TableCell>{t('vetCheckup.severity', 'الشدة')}</TableCell>
                  <TableCell>
                    {t('vetReport.actionRequired', 'يتطلب إجراء')}
                  </TableCell>
                  {/* <TableCell>{t('vetReport.case', 'الحالة')}</TableCell>*/}
                  <TableCell>{t('vetReport.vet', 'الطبيب')}</TableCell>
                  <TableCell>{t('vetReport.notes', 'ملاحظات')}</TableCell>
                  <TableCell align="center" sx={{ width: 230 }}>{t('common.actions', 'إبلاغ المالك')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedRounds.map((r, idx) => {
                  const items = Array.isArray(r.items) ? r.items : [];
                  const horses = getHorseLabel(items);
                  const worst = worstSeverityOf(items);
                  const needsAction = anyActionRequired(items);
                  return (
                    <TableRow
                      key={r.round_id}
                      hover
                      sx={(theme) => ({
                        cursor: items.length ? 'pointer' : 'default',
                        ...(needsAction
                          ? {
                              backgroundColor: alpha(
                                theme.palette.error.light,
                                0.06
                              ),
                            }
                          : worst && worst.rank >= 3
                          ? {
                              backgroundColor: alpha(
                                theme.palette.warning.light,
                                0.06
                              ),
                            }
                          : {}),
                      })}
                      onClick={() => {
                        if (items.length) {
                          setItemsOfRound({ round: r, items });
                          // تتبع فتح تفاصيل الجولة
                          logAudit({
                            entity_type: 'vet_checkup_round',
                            entity_id: r.round_id,
                            action: 'VIEW',
                          });
                        }
                      }}
                    >
                      {/* الترقيم */}
                      <TableCell sx={{ color: COLORS.muted }}>{page * rowsPerPage + idx + 1}</TableCell>

                      <TableCell><span className="date-ltr">{formatDateTime(r.visit_date)}</span></TableCell>
                      <TableCell>{r.stable_name || r.stable_id}</TableCell>
                      <TableCell>{r.room_name || r.room_id}</TableCell>
                      <TableCell sx={{ fontWeight: 900, color: COLORS.text }}>{horses || '—'}</TableCell>

                      {/* الشدة (أعلى شدة في الجولة) */}
                      <TableCell>
                        {worst ? (
                          <Chip
                            size="small"
                            color={worst.color}
                            label={worst.label}
                          />
                        ) : (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={t('common.no', 'لا')}
                          />
                        )}
                      </TableCell>

                      {/* يتطلب إجراء */}
                      <TableCell>
                        <Chip
                          size="small"
                          label={
                            needsAction
                              ? t('common.yes', 'نعم')
                              : t('common.no', 'لا')
                          }
                          color={needsAction ? 'error' : 'default'}
                          variant={needsAction ? 'filled' : 'outlined'}
                        />
                      </TableCell>

                      {/* الحالة */}
                      {/*  <TableCell>
                      {items.length
                        ? items
                            .map(
                              (it) =>
                                it.type_name_ar || it.type_name_en || it.type_id
                            )
                            .filter(Boolean)
                            .join(', ')
                        : '—'}
                    </TableCell>
*/}
                      <TableCell>
                        {r.vet_name || r.vet_user_id || '—'}
                      </TableCell>
                      <TableCell
                        sx={{
                          maxWidth: 320,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {r.notes || '—'}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} justifyContent="center">
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={
                              !items.length ||
                              sending.has(String(r.round_id ?? r.id))
                            }
                            startIcon={dir === 'ltr' ? <Email fontSize="small" /> : null}
                            endIcon={dir === 'rtl' ? <Email fontSize="small" /> : null}
                            onClick={(e) => {
                              e.stopPropagation();
                              notifyOwnersForRound(r);
                            }}
                            sx={{ borderColor: COLORS.line, color: COLORS.text, fontWeight: 900 }}
                          >
                            {t('vetReport.byemail', 'By email')}
                          </Button>

                          <Button
                            size="small"
                            variant="outlined"
                            disabled={!items.length}
                            startIcon={dir === 'ltr' ? <WhatsApp fontSize="small" /> : null}
                            endIcon={dir === 'rtl' ? <WhatsApp fontSize="small" /> : null}
                            onClick={(e) => {
                              e.stopPropagation();
                              sendWhatsAppForRound(r);
                            }}
                            sx={{ borderColor: COLORS.line, color: COLORS.green, fontWeight: 900 }}
                          >
                            {t('vetReport.whatsapp', 'by whatsapp')}
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
              </TableContainer>

            <TablePagination
              component="div"
              count={rounds.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage={t('common.rowsPerPage', 'صفوف لكل صفحة')}
              sx={{ borderTop: `1px solid ${COLORS.line}` }}
            />
            </>
          )}
        </Paper>

        {/* Items dialog */}
        <Dialog
          open={!!itemsOfRound}
          onClose={() => setItemsOfRound(null)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {t('vetReport.itemsOfRound', 'عناصر الجولة')} — #
            {itemsOfRound?.round?.round_id}
          </DialogTitle>
          <DialogContent dividers>
            {!itemsOfRound ||
            !Array.isArray(itemsOfRound.items) ||
            itemsOfRound.items.length === 0 ? (
              <Alert severity="info" sx={{ my: 1 }}>
                {t('vetReport.noItems', 'لا توجد عناصر مرتبطة بهذه الجولة')}
              </Alert>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>{t('vetReport.horse', 'الخيل')}</TableCell>
                    <TableCell>{t('vetReport.case', 'الحالة')}</TableCell>
                    <TableCell>{t('vetCheckup.severity', 'الشدة')}</TableCell>
                    <TableCell>
                      {t('vetReport.actionRequired', 'يتطلب إجراء')}
                    </TableCell>
                    <TableCell>{t('vetReport.followUp', 'متابعة')}</TableCell>
                    <TableCell>
                      {t('vetReport.itemNotes', 'ملاحظات الحالة')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {itemsOfRound.items.map((it) => (
                    <TableRow
                      key={it.item_id || `${it.horse_id}-${it.type_id}`}
                    >
                      <TableCell>{it.item_id || '—'}</TableCell>
                      <TableCell>
                        {it.horse_name ||
                          it.horse_name_ar ||
                          it.horse_name_en ||
                          it.horse ||
                          it.horse_id}
                      </TableCell>
                      <TableCell>
                        {it.type_name_ar || it.type_name_en || it.type_id}
                      </TableCell>
                      <TableCell>{it.severity || 'low'}</TableCell>
                      <TableCell>
                        {Number(it.action_required)
                          ? t('common.yes', 'نعم')
                          : t('common.no', 'لا')}
                      </TableCell>
                      <TableCell>{it.follow_up_date ? <span className="date-ltr">{formatDateDisplay(it.follow_up_date)}</span> : '—'}</TableCell>
                      <TableCell
                        sx={{
                          maxWidth: 360,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {it.notes || '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </DialogContent>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          autoHideDuration={snackbar.sev === 'error' ? 5000 : 2500}
        >
          <Alert
            severity={snackbar.sev}
            onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          >
            {snackbar.msg}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );
}
